import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ArrowUpOnSquareIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form } from 'softcat:components/form.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { CatalogueService } from 'softcat:services/catalogue.ts';
import { LocationService } from 'softcat:services/location.ts';
import useSWR from 'swr';
import * as z from 'zod';

type CataloguePublishModalProperties = {
    handleClose: () => void;
    isVisible: boolean;
    name: string;
    version: string;
};

export const CataloguePublishModal: FunctionComponent<CataloguePublishModalProperties> = ({ handleClose, isVisible, name, version }) => {
    const intl = useIntl();
    const { activeTerritory } = useTerritory();
    const [submitting, setSubmitting] = useState(false);
    const [onPublishError, setOnPublishError] = useState('');
    const certificateFileExtension = '.p12';

    const PublishCatalogueSchema = z.object({
        certificate: z.instanceof(File, { message: intl.formatMessage({ id: 'CATALOGUES_PAGE_CONFIRM_FORM_CERTIFICATE_VALIDATION_REQUIRED' }) }).refine(
            (file) => {
                return file.name.endsWith(certificateFileExtension);
            },
            { message: intl.formatMessage({ id: 'FORM_FILE_UPLOAD_VALIDATION_EXTENSION' }, { extensions: certificateFileExtension }) },
        ),
        location: z.string().min(1, { message: intl.formatMessage({ id: 'CATALOGUES_PAGE_CONFIRM_FORM_LOCATION_VALIDATION_REQUIRED' }) }),
        password: z.string().min(1, { message: intl.formatMessage({ id: 'CATALOGUES_PAGE_CONFIRM_FORM_PASSWORD_VALIDATION_REQUIRED' }) }),
    });

    const {
        data: catalogueData,
        error: catalogueDataError,
        isLoading: catalogueDataIsLoading,
    } = useSWR({ key: `${activeTerritory}-CatalogueServiceFindOne`, name, version }, CatalogueService.findOne);

    const onSubmit = async (values: z.infer<typeof PublishCatalogueSchema>) => {
        setSubmitting(true);
        setOnPublishError('');

        try {
            await CatalogueService.publish({
                ...values,
                name,
                version,
            });

            handleClose();
        } catch (error) {
            const message = (error as Error).message;

            if (message.includes('Failed to setup credentials for signing')) {
                setOnPublishError(intl.formatMessage({ id: 'CATALOGUES_PAGE_CONFIRM_PUBLISH_SUBMIT_ERROR_INVALID_PASSWORD' }));
            } else {
                setOnPublishError(message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (!isVisible) {
            setOnPublishError('');
        }
    }, [isVisible]);

    const defaultValues = useMemo(() => {
        return { certificate: undefined, location: '', password: '' };
    }, []);

    return (
        <Dialog className="relative z-[60]" data-testid={'catalogue-publish-modal'} onClose={handleClose} open={isVisible}>
            <DialogBackdrop
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                transition
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-2xl data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                        transition
                    >
                        <Form<z.infer<typeof PublishCatalogueSchema>>
                            defaultValues={defaultValues}
                            onSubmit={onSubmit}
                            resolver={zodResolver(PublishCatalogueSchema)}
                        >
                            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <ArrowUpOnSquareIcon aria-hidden="true" className="h-6 w-6 text-indigo-600" />
                                    </div>
                                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <DialogTitle as={'div'}>
                                                <Form.Header
                                                    description={<FormattedMessage id={'CATALOGUES_PAGE_CONFIRM_PUBLISH_DESCRIPTION'} />}
                                                    title={<FormattedMessage id={'CATALOGUES_PAGE_CONFIRM_PUBLISH_TITLE'} />}
                                                />
                                            </DialogTitle>
                                        </div>
                                        <div className="mt-3 min-h-56 space-y-3 sm:ml-4">
                                            {catalogueDataError && (
                                                <div>
                                                    <FormattedMessage id={'CATALOGUES_PAGE_CONFIRM_ERROR_LOADING_DATA'} />
                                                </div>
                                            )}

                                            {catalogueDataIsLoading && <Spinner />}

                                            {catalogueData && (
                                                <>
                                                    <Form.RemoteSearchCombobox
                                                        cache={`${activeTerritory}-LocationServiceFindAll`}
                                                        label={<FormattedMessage id={'CATALOGUES_PAGE_CONFIRM_FORM_LOCATION_LABEL'} />}
                                                        name={'location'}
                                                        options={(data) => {
                                                            return data.map((location) => {
                                                                return { label: location.name, value: location.name };
                                                            });
                                                        }}
                                                        placeholder={intl.formatMessage({ id: 'CATALOGUES_PAGE_CONFIRM_FORM_LOCATION_PLACEHOLDER' })}
                                                        service={LocationService.findAll}
                                                    />

                                                    <Form.FileUpload
                                                        accept={certificateFileExtension}
                                                        label={<FormattedMessage id={'CATALOGUES_PAGE_CONFIRM_FORM_CERTIFICATE_LABEL'} />}
                                                        name={'certificate'}
                                                    />

                                                    <Form.Input
                                                        label={<FormattedMessage id={'CATALOGUES_PAGE_CONFIRM_FORM_PASSWORD_LABEL'} />}
                                                        name={'password'}
                                                        type={'password'}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Form.Footer
                                disabled={catalogueDataError || !catalogueData?.packages || catalogueData.packages.length === 0}
                                isSubmitting={submitting}
                                onCancel={handleClose}
                                submitError={
                                    onPublishError && (
                                        <FormattedMessage id={'CATALOGUES_PAGE_CONFIRM_PUBLISH_SUBMIT_ERROR'} values={{ error: onPublishError }} />
                                    )
                                }
                                submitText={<FormattedMessage id={'BUTTON_PUBLISH'} />}
                            />
                        </Form>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
};
